/* eslint-disable */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  Calendar, ChevronRight, X, Check, Clock, Users,
  Send, Plus, AlertTriangle, CheckCircle, Edit2, ChevronDown,
} from 'lucide-react';

const C = {
  cream:'#F7F6EF', charcoal:'#2F3A42', navy:'#445366', mid:'#7A8A96',
  border:'#D8DEE3', skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  green:'#7EA594', greenLight:'#EAF2EF',
  orange:'#E4AC49', orangeLight:'#FDF3DC',
  amber:'#E07B00', amberLight:'#FFF3E0',
  red:'#DC3545', redLight:'#FFF0F0',
  white:'#FFFFFF', gold:'#E4AC49', goldLight:'#FDF3DC',
  teal:'#0D9488', tealLight:'#E6FAF8',
};

const VENUE_IDENTITY = {
  'Box Jordaan':  { bg:'#EDF3FC', text:'#2563EB', border:'#BAD4F5', short:'Jordaan'  },
  'Box Plantage': { bg:'#C8E6D8', text:'#2D7A65', border:'#7EA594', short:'Plantage' },
};

// ── Tab config — three clear statuses ────────────────────────────────────────
const TABS = [
  { key:'enquiry_sent',   label:'Form Sent to Guest',  color:C.skyText, bg:C.skyBlueLight },
  { key:'form_submitted', label:'Submitted Forms',      color:C.amber,   bg:C.amberLight   },
  { key:'confirmed',      label:'Confirmed Events',     color:C.green,   bg:C.greenLight   },
];

const EVENT_TYPE_LABELS = {
  birthday:'Birthday', corporate:'Corporate', brunch:'Brunch Party',
  engagement:'Engagement', staff_party:'Staff Party',
  whole_venue_only:'Whole Venue', other:'Other',
};

const OPS_CHECKLIST = [
  { key:'deposit_paid',         label:'Deposit Paid'                },
  { key:'tebi_invoice_sent',    label:'Tebi Invoice Sent'           },
  { key:'staffing_confirmed',   label:'Staffing Scheduled'          },
  { key:'food_confirmed',       label:'Food Package Confirmed'      },
  { key:'headcount_finalised',  label:'Headcount Finalised'         },
  { key:'special_req_actioned', label:'Special Requirements Noted'  },
  { key:'post_event_done',      label:'Post-Event Notes Added'      },
];

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

function fmtDate(ds) {
  if (!ds) return '—';
  return new Date(ds + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short', year:'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
}
function daysUntil(ds) {
  if (!ds) return null;
  const t = new Date(); t.setHours(0,0,0,0);
  const d = new Date(ds + 'T00:00:00'); d.setHours(0,0,0,0);
  return Math.floor((d - t) / 86400000);
}

// ── Send Form Modal ───────────────────────────────────────────────────────────
function SendFormModal({ currentUser, onClose, onSent }) {
  const [email, setEmail]     = useState('');
  const [venue, setVenue]     = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');
  const formUrl = `${window.location.origin}/event-enquiry`;

  async function handleSend() {
    if (!email.trim()) { setError('Please enter an email address.'); return; }
    if (!venue)        { setError('Please select a venue.'); return; }
    setSending(true); setError('');

    const { error: dbErr } = await supabase.from('group_events').insert({
      venue,
      enquiry_sent_to: email.trim(),
      enquiry_sent_by: currentUser?.name || 'Manager',
      enquiry_sent_at: new Date().toISOString(),
      status:          'enquiry_sent',
      created_by:      currentUser?.id || null,
    });
    if (dbErr) { setError('Failed to log: ' + dbErr.message); setSending(false); return; }

    const { error: fnErr } = await supabase.functions.invoke('send-event-enquiry-email', {
      body: { type:'send_form', to:email.trim(), formUrl, sentBy:currentUser?.name || 'Manager' },
    });
    if (fnErr) setError('Email failed — share this link manually: ' + formUrl);

    setSent(true); setSending(false);
    if (onSent) onSent();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(44,58,66,0.6)', zIndex:900, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={onClose}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'420px', overflow:'hidden', boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background:C.navy, padding:'18px 20px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>Send Enquiry Form</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>Customer receives a link to complete their event details</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={16} /></button>
        </div>
        <div style={{ padding:'20px' }}>
          {sent ? (
            <div style={{ textAlign:'center', padding:'12px 0' }}>
              <CheckCircle size={36} color={C.green} style={{ marginBottom:'10px' }} />
              <div style={{ fontWeight:700, fontSize:'15px', color:C.charcoal, marginBottom:'6px' }}>Form sent</div>
              <div style={{ fontSize:'13px', color:C.mid }}>Enquiry logged and email sent to <strong>{email}</strong></div>
              <button onClick={onClose} style={{ marginTop:'16px', padding:'10px 24px', background:C.navy, color:C.white, border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:600, fontSize:'14px' }}>Done</button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'12px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' }}>Venue</div>
                <div style={{ display:'flex', gap:'8px' }}>
                  {['Box Jordaan','Box Plantage'].map(v => {
                    const vi = VENUE_IDENTITY[v];
                    return (
                      <button key={v} onClick={() => setVenue(v)}
                        style={{ flex:1, padding:'9px', fontSize:'13px', fontWeight:700, border:`1.5px solid ${venue===v ? vi.border : C.border}`, borderRadius:'8px', cursor:'pointer', background: venue===v ? vi.bg : C.white, color: venue===v ? vi.text : C.mid, transition:'all 0.15s' }}>
                        {vi.short}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'12px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' }}>Customer Email</div>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="customer@example.com"
                  style={{ width:'100%', boxSizing:'border-box', padding:'10px 12px', fontSize:'14px', border:`1.5px solid ${C.border}`, borderRadius:'8px', outline:'none', fontFamily:'inherit', color:C.charcoal }}
                  onFocus={e => e.target.style.borderColor = C.gold}
                  onBlur={e  => e.target.style.borderColor = C.border} />
              </div>
              {error && <div style={{ fontSize:'12px', color:C.red, background:C.redLight, borderRadius:'6px', padding:'8px 12px', marginBottom:'12px' }}>{error}</div>}
              <button onClick={handleSend} disabled={sending}
                style={{ width:'100%', padding:'12px', fontSize:'14px', fontWeight:700, background: sending ? C.border : C.gold, color: sending ? C.mid : C.charcoal, border:'none', borderRadius:'8px', cursor: sending ? 'not-allowed' : 'pointer' }}>
                {sending ? 'Sending…' : 'Send Form Link'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Event Modal ───────────────────────────────────────────────────────────────
function EventModal({ event, currentUser, onClose, onUpdate }) {
  const [ev, setEv]               = useState(event);
  const [editing, setEditing]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [notes, setNotes]         = useState(event.internal_notes || '');
  const [postNotes, setPostNotes] = useState(event.post_event_notes || '');
  const [tab, setTab]             = useState('details');

  const role    = currentUser?.role;
  const canEdit = role === 'admin' || role === 'manager';
  const vi      = VENUE_IDENTITY[ev.venue] || {};

  const doneCount = OPS_CHECKLIST.filter(c => ev[c.key]).length;
  const allDone   = doneCount === OPS_CHECKLIST.length;

  // Days until — based on event_date, not submission date
  const days = daysUntil(ev.event_date);
  const daysLabel = days === null ? null
    : days === 0   ? 'Today'
    : days === 1   ? 'Tomorrow'
    : days < 0     ? `${Math.abs(days)} days ago`
    : `In ${days} days`;

  async function toggleCheck(key) {
    if (!canEdit) return;
    const newVal  = !ev[key];
    const updated = { ...ev, [key]: newVal };
    setEv(updated);
    await supabase.from('group_events').update({ [key]: newVal }).eq('id', ev.id);
    if (onUpdate) onUpdate(updated);
  }

  async function updateStatus(newStatus) {
    if (!canEdit) return;
    const patch = { status: newStatus };
    if (newStatus === 'confirmed' && !ev.confirmed_at) patch.confirmed_at = new Date().toISOString();
    const updated = { ...ev, ...patch };
    setEv(updated);
    await supabase.from('group_events').update(patch).eq('id', ev.id);
    if (onUpdate) onUpdate(updated);

    if (newStatus === 'confirmed' && ev.event_date) {
      await supabase.from('calendar_events').insert({
        title: `${EVENT_TYPE_LABELS[ev.event_type] || 'Group Event'} — ${ev.contact_name || 'Guest'} (${ev.guest_count || '?'} guests)`,
        date:  ev.event_date,
        type:  'group_event',
        venue: ev.venue,
      });
    }
  }

  async function saveNotes() {
    setSaving(true);
    const patch = { internal_notes: notes, post_event_notes: postNotes };
    await supabase.from('group_events').update(patch).eq('id', ev.id);
    const updated = { ...ev, ...patch };
    setEv(updated);
    if (onUpdate) onUpdate(updated);
    setEditing(false); setSaving(false);
  }

  const packages = Array.isArray(ev.packages) ? ev.packages : [];

  const tabCfg = TABS.find(t => t.key === ev.status) || TABS[0];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(44,58,66,0.6)', zIndex:900, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'5vh 16px 16px', overflowY:'auto' }}
      onClick={onClose}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'560px', boxShadow:'0 24px 64px rgba(0,0,0,0.2)', overflow:'hidden', marginBottom:'16px' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ background:C.navy, padding:'18px 20px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'12px' }}>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'5px', flexWrap:'wrap' }}>
                <span style={{ fontSize:'10px', fontWeight:700, color:vi.text, background:vi.bg, padding:'2px 8px', borderRadius:'20px' }}>{vi.short}</span>
                <span style={{ fontSize:'10px', fontWeight:700, color:tabCfg.color, background:tabCfg.bg, padding:'2px 8px', borderRadius:'20px' }}>{tabCfg.label}</span>
              </div>
              <div style={{ fontWeight:800, fontSize:'17px', color:C.white, marginBottom:'2px' }}>
                {ev.contact_name || 'Unnamed Event'}
              </div>
              <div style={{ fontSize:'13px', color:C.skyBlue }}>
                {ev.event_type ? (EVENT_TYPE_LABELS[ev.event_type] || ev.event_type) : 'Group Event'}
                {ev.guest_count ? ` · ${ev.guest_count} guests` : ''}
              </div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex', flexShrink:0 }}><X size={16} /></button>
          </div>

          {/* Date + days until */}
          {ev.event_date && (
            <div style={{ marginTop:'12px', background:'rgba(255,255,255,0.1)', borderRadius:'8px', padding:'9px 12px', display:'inline-flex', alignItems:'center', gap:'8px' }}>
              <Calendar size={13} color={C.skyBlue} />
              <span style={{ fontSize:'13px', fontWeight:700, color:C.white }}>{fmtDate(ev.event_date)}</span>
              {ev.event_time && <span style={{ fontSize:'12px', color:C.skyBlue }}>· {ev.event_time}</span>}
              {daysLabel && (
                <span style={{ fontSize:'11px', fontWeight:700, color: days !== null && days <= 7 && days >= 0 ? '#F7EC71' : C.skyBlue }}>
                  · {daysLabel}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, background:C.cream }}>
          {[['details','Details'], ['ops', `Ops (${doneCount}/${OPS_CHECKLIST.length})`]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ flex:1, padding:'11px', fontSize:'12px', fontWeight:700, border:'none', borderBottom:`2.5px solid ${tab===key ? C.navy : 'transparent'}`, background:'none', cursor:'pointer', color: tab===key ? C.navy : C.mid, transition:'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding:'18px 20px', maxHeight:'60vh', overflowY:'auto' }}>

          {tab === 'details' && (
            <div>
              <Section title="Contact">
                <Row label="Name"   value={ev.contact_name} />
                <Row label="Email"  value={ev.contact_email ? <a href={`mailto:${ev.contact_email}`} style={{ color:C.skyText }}>{ev.contact_email}</a> : null} />
                <Row label="Phone"  value={ev.contact_phone} />
              </Section>

              {packages.length > 0 && (
                <Section title="Packages">
                  <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                    {packages.map((p,i) => (
                      <div key={i} style={{ fontSize:'13px', color:C.charcoal, background:C.cream, borderRadius:'6px', padding:'7px 10px' }}>{p}</div>
                    ))}
                  </div>
                </Section>
              )}

              {ev.special_requirements && (
                <Section title="Special Requirements">
                  <div style={{ fontSize:'13px', color:C.charcoal, lineHeight:1.6, background:C.amberLight, border:`1px solid ${C.amber}`, borderRadius:'6px', padding:'9px 12px' }}>
                    {ev.special_requirements}
                  </div>
                </Section>
              )}

              {canEdit && (
                <Section title="Tracking">
                  <Row label="Sent to"   value={ev.enquiry_sent_to} />
                  <Row label="Sent by"   value={ev.enquiry_sent_by} />
                  <Row label="Sent at"   value={fmtDateTime(ev.enquiry_sent_at)} />
                  <Row label="Submitted" value={fmtDateTime(ev.form_submitted_at)} />
                  {ev.confirmed_at && <Row label="Confirmed" value={fmtDateTime(ev.confirmed_at)} />}
                </Section>
              )}

              {canEdit && (
                <Section title="Notes"
                  action={
                    <button onClick={() => setEditing(p => !p)}
                      style={{ background:'none', border:'none', cursor:'pointer', color:C.skyText, fontSize:'12px', fontWeight:700, display:'flex', alignItems:'center', gap:'3px' }}>
                      <Edit2 size={11} /> {editing ? 'Cancel' : 'Edit'}
                    </button>
                  }>
                  {editing ? (
                    <>
                      <div style={{ marginBottom:'10px' }}>
                        <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Internal Notes</div>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                          style={{ width:'100%', boxSizing:'border-box', padding:'9px 12px', fontSize:'13px', border:`1.5px solid ${C.border}`, borderRadius:'8px', fontFamily:'inherit', resize:'vertical', outline:'none' }} />
                      </div>
                      <div style={{ marginBottom:'10px' }}>
                        <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Post-Event Notes</div>
                        <textarea value={postNotes} onChange={e => setPostNotes(e.target.value)} rows={3}
                          placeholder="How did it go? Any improvements needed?"
                          style={{ width:'100%', boxSizing:'border-box', padding:'9px 12px', fontSize:'13px', border:`1.5px solid ${C.border}`, borderRadius:'8px', fontFamily:'inherit', resize:'vertical', outline:'none' }} />
                      </div>
                      <button onClick={saveNotes} disabled={saving}
                        style={{ padding:'9px 18px', fontSize:'13px', fontWeight:700, background:C.navy, color:C.white, border:'none', borderRadius:'8px', cursor:'pointer' }}>
                        {saving ? 'Saving…' : 'Save Notes'}
                      </button>
                    </>
                  ) : (
                    <>
                      {ev.internal_notes
                        ? <div style={{ fontSize:'13px', color:C.charcoal, lineHeight:1.6, background:C.cream, borderRadius:'6px', padding:'9px 12px', marginBottom:'8px' }}>{ev.internal_notes}</div>
                        : <div style={{ fontSize:'13px', color:C.mid, marginBottom:'8px' }}>No internal notes yet.</div>}
                      {ev.post_event_notes && (
                        <>
                          <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'4px' }}>Post-Event</div>
                          <div style={{ fontSize:'13px', color:C.charcoal, lineHeight:1.6, background:C.greenLight, border:`1px solid ${C.green}`, borderRadius:'6px', padding:'9px 12px' }}>{ev.post_event_notes}</div>
                        </>
                      )}
                    </>
                  )}
                </Section>
              )}
            </div>
          )}

          {tab === 'ops' && (
            <div>
              {ev.status !== 'confirmed' && canEdit && (
                <div style={{ background:C.amberLight, border:`1px solid ${C.amber}`, borderRadius:'8px', padding:'10px 14px', marginBottom:'16px', fontSize:'13px', color:C.amber, fontWeight:600 }}>
                  Confirm this event to enable the full ops checklist
                </div>
              )}
              {allDone && ev.status === 'confirmed' && (
                <div style={{ background:C.greenLight, border:`1px solid ${C.green}`, borderRadius:'8px', padding:'10px 14px', marginBottom:'16px', fontSize:'13px', color:C.green, fontWeight:600 }}>
                  All ops checks complete — you're ready
                </div>
              )}
              <div style={{ display:'flex', flexDirection:'column', gap:'4px', marginBottom:'20px' }}>
                {OPS_CHECKLIST.map(item => {
                  const done = !!ev[item.key];
                  return (
                    <div key={item.key} onClick={() => canEdit && toggleCheck(item.key)}
                      style={{ display:'flex', alignItems:'center', gap:'12px', padding:'11px 14px', borderRadius:'8px', background: done ? C.greenLight : C.cream, cursor: canEdit ? 'pointer' : 'default', border:`1px solid ${done ? C.green : C.border}`, transition:'background 0.15s' }}>
                      <div style={{ width:'20px', height:'20px', borderRadius:'50%', border:`2px solid ${done ? C.green : C.border}`, background: done ? C.green : C.white, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s' }}>
                        {done && <Check size={11} color={C.white} strokeWidth={3} />}
                      </div>
                      <span style={{ fontSize:'14px', fontWeight: done ? 600 : 400, color: done ? C.charcoal : C.mid }}>{item.label}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginBottom:'4px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
                  <span style={{ fontSize:'12px', fontWeight:700, color:C.mid }}>Event readiness</span>
                  <span style={{ fontSize:'12px', fontWeight:700, color:C.charcoal }}>{doneCount}/{OPS_CHECKLIST.length}</span>
                </div>
                <div style={{ height:'6px', background:C.border, borderRadius:'4px', overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${(doneCount/OPS_CHECKLIST.length)*100}%`, background: allDone ? C.green : C.gold, borderRadius:'4px', transition:'width 0.3s' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {canEdit && (
          <div style={{ padding:'14px 20px', borderTop:`1px solid ${C.border}`, background:C.cream, display:'flex', gap:'8px', flexWrap:'wrap' }}>
            {ev.status === 'form_submitted' && (
              <button onClick={() => updateStatus('confirmed')}
                style={{ flex:1, padding:'10px', fontSize:'13px', fontWeight:700, background:C.green, color:C.white, border:'none', borderRadius:'8px', cursor:'pointer' }}>
                Confirm Event
              </button>
            )}
            {ev.status === 'enquiry_sent' && (
              <div style={{ flex:1, fontSize:'12px', color:C.mid, display:'flex', alignItems:'center' }}>
                Waiting for customer to complete the form
              </div>
            )}
            {ev.status === 'confirmed' && (
              <div style={{ flex:1, fontSize:'12px', color:C.green, fontWeight:600, display:'flex', alignItems:'center', gap:'5px' }}>
                <CheckCircle size={13} /> Confirmed
                {ev.confirmed_at && <span style={{ color:C.mid, fontWeight:400 }}>· {fmtDateTime(ev.confirmed_at)}</span>}
              </div>
            )}
            {ev.status !== 'cancelled' && (
              <button onClick={() => updateStatus('cancelled')}
                style={{ padding:'10px 16px', fontSize:'13px', fontWeight:600, background:'none', color:C.mid, border:`1px solid ${C.border}`, borderRadius:'8px', cursor:'pointer' }}>
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function Section({ title, children, action }) {
  return (
    <div style={{ marginBottom:'18px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
        <div style={{ fontSize:'10px', fontWeight:800, color:C.mid, textTransform:'uppercase', letterSpacing:'0.07em' }}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display:'flex', gap:'12px', padding:'6px 0', borderBottom:`1px solid ${C.border}`, fontSize:'13px' }}>
      <span style={{ color:C.mid, minWidth:'90px', flexShrink:0 }}>{label}</span>
      <span style={{ color:C.charcoal }}>{value}</span>
    </div>
  );
}

// ── Event Card ────────────────────────────────────────────────────────────────
function EventCard({ event, onClick }) {
  const vi       = VENUE_IDENTITY[event.venue] || {};
  const tabCfg   = TABS.find(t => t.key === event.status) || {};
  const days     = daysUntil(event.event_date);
  const packages = Array.isArray(event.packages) ? event.packages : [];
  const doneCount = OPS_CHECKLIST.filter(c => event[c.key]).length;

  return (
    <div onClick={onClick}
      style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:'10px', padding:'14px 16px', cursor:'pointer', transition:'box-shadow 0.15s', marginBottom:'8px' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.09)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'10px' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'4px', flexWrap:'wrap' }}>
            <span style={{ fontSize:'10px', fontWeight:700, color:vi.text, background:vi.bg, padding:'1px 7px', borderRadius:'20px' }}>{vi.short}</span>
            {event.event_type && <span style={{ fontSize:'11px', color:C.mid }}>{EVENT_TYPE_LABELS[event.event_type] || event.event_type}</span>}
          </div>
          <div style={{ fontSize:'14px', fontWeight:700, color:C.charcoal, marginBottom:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {event.contact_name || event.enquiry_sent_to || 'Unnamed'}
            {event.guest_count ? ` · ${event.guest_count} guests` : ''}
          </div>
          <div style={{ fontSize:'12px', color:C.mid }}>
            {event.event_date ? fmtDate(event.event_date) : 'Date not yet provided'}
            {days !== null && event.event_date && (
              <span style={{ marginLeft:'6px', color: days <= 7 && days >= 0 ? C.amber : C.mid, fontWeight: days <= 7 && days >= 0 ? 700 : 400 }}>
                · {days === 0 ? 'Today' : days < 0 ? `${Math.abs(days)}d ago` : `in ${days}d`}
              </span>
            )}
          </div>
          {packages.length > 0 && (
            <div style={{ fontSize:'11px', color:C.mid, marginTop:'4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {packages.join(' · ')}
            </div>
          )}
          {/* Show sent-to email for enquiry_sent cards */}
          {event.status === 'enquiry_sent' && event.enquiry_sent_to && (
            <div style={{ fontSize:'11px', color:C.skyText, marginTop:'3px' }}>{event.enquiry_sent_to}</div>
          )}
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'6px', flexShrink:0 }}>
          {event.status === 'confirmed' && (
            <span style={{ fontSize:'10px', color: doneCount===OPS_CHECKLIST.length ? C.green : C.amber, fontWeight:600 }}>
              {doneCount}/{OPS_CHECKLIST.length} ops
            </span>
          )}
          <ChevronRight size={14} color={C.mid} />
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Events({ currentUser }) {
  const [events, setEvents]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState('form_submitted');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showSendForm, setShowSendForm]   = useState(false);
  const isMobile = useIsMobile();

  const role    = currentUser?.role;
  const canEdit = role === 'admin' || role === 'manager';

  async function loadEvents() {
    setLoading(true);
    let q = supabase.from('group_events')
      .select('*')
      .neq('status', 'cancelled')
      .order('created_at', { ascending: false });
    // Staff/supervisors only see confirmed
    if (!canEdit) q = q.eq('status', 'confirmed');
    const { data } = await q;
    setEvents(data || []);
    setLoading(false);
  }

  useEffect(() => { loadEvents(); }, []);

  function handleUpdate(updated) {
    setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
    if (selectedEvent?.id === updated.id) setSelectedEvent(updated);
  }

  // Filter strictly by current status — records move between tabs as status changes
  const byTab = {
    enquiry_sent:   events.filter(e => e.status === 'enquiry_sent'),
    form_submitted: events.filter(e => e.status === 'form_submitted'),
    confirmed:      events.filter(e => e.status === 'confirmed'),
  };

  const filtered = byTab[filter] || [];

  return (
    <div style={{ minHeight:'100vh', background:'#EAF4F8' }}>
      <div style={{ padding: isMobile ? '1rem' : '24px', maxWidth:'720px', margin:'0 auto' }}>

        {/* Title */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
          <div>
            <div style={{ fontSize: isMobile ? '20px' : '22px', fontWeight:800, color:'#445366' }}>Events</div>
            <div style={{ fontSize:'13px', color:'#3A8FA3', marginTop:'2px', fontWeight:500 }}>Group bookings and private events</div>
          </div>
          {canEdit && (
            <button onClick={() => setShowSendForm(true)}
              style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 14px', fontSize:'13px', fontWeight:700, background:C.navy, color:C.white, border:'none', borderRadius:'8px', cursor:'pointer' }}>
              <Plus size={14} /> New Enquiry
            </button>
          )}
        </div>

        {/* Filter tabs — MTD card style */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'16px' }}>
          {TABS.map(tab => {
            const count   = byTab[tab.key]?.length || 0;
            const active  = filter === tab.key;
            return (
              <div key={tab.key} onClick={() => setFilter(tab.key)}
                style={{ background:C.white, border:`2px solid ${active ? tab.color : C.border}`, borderRadius:'10px', padding:'12px 10px', cursor:'pointer', transition:'all 0.15s', textAlign:'center' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ fontSize: isMobile ? '22px' : '26px', fontWeight:800, color: active ? tab.color : count > 0 ? C.charcoal : C.mid, marginBottom:'2px' }}>
                  {count}
                </div>
                <div style={{ fontSize:'10px', fontWeight:700, color: active ? tab.color : C.mid, textTransform:'uppercase', letterSpacing:'0.04em', lineHeight:1.3 }}>
                  {tab.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* List */}
        {loading ? (
          <div style={{ padding:'24px', textAlign:'center', color:C.mid, fontSize:'14px' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'32px', textAlign:'center' }}>
            <div style={{ fontSize:'14px', fontWeight:600, color:C.charcoal, marginBottom:'4px' }}>
              {filter==='enquiry_sent'   ? 'No forms sent yet'
               : filter==='form_submitted' ? 'All caught up'
               : 'No confirmed events yet'}
            </div>
            <div style={{ fontSize:'13px', color:C.mid }}>
              {filter==='enquiry_sent'   ? 'Use "New Enquiry" to send a form to a customer'
               : filter==='form_submitted' ? 'No submitted forms waiting for action'
               : 'Confirmed events will appear here'}
            </div>
          </div>
        ) : (
          filtered.map(ev => (
            <EventCard key={ev.id} event={ev} onClick={() => setSelectedEvent(ev)} />
          ))
        )}
      </div>

      {showSendForm && (
        <SendFormModal currentUser={currentUser} onClose={() => setShowSendForm(false)} onSent={loadEvents} />
      )}
      {selectedEvent && (
        <EventModal event={selectedEvent} currentUser={currentUser} onClose={() => setSelectedEvent(null)} onUpdate={handleUpdate} />
      )}
    </div>
  );
}